import { useState } from "react";

/**
 * A code block with window chrome, real highlighting and a copy button.
 *
 * The highlighter is hand-rolled and about eighty lines. Every off-the-shelf
 * one is tens of kilobytes for a page that shows four snippets, and this file
 * only ever has to colour the four languages we actually print — the moment it
 * needs a fifth, that is the moment to reconsider.
 *
 * Deliberately dark in both themes, the way Neon, Supabase and Stripe all print
 * code. A code block that inverts with the page loses the one visual anchor a
 * reader has for "this is a thing you paste", and the token colours have to be
 * redesigned twice to keep their contrast.
 *
 * Tokens are emitted as React elements rather than an HTML string, so nothing
 * here can turn a snippet into markup.
 */

export type Lang = "bash" | "sql" | "json" | "http";

interface Rule {
  /** Sticky, so it can be tried at an exact offset. */
  re: RegExp;
  cls: string;
}

const rule = (source: string, cls: string): Rule => ({
  re: new RegExp(source, "y"),
  cls,
});

/**
 * Order matters: comments and strings come first so a keyword inside either is
 * not coloured as one, and the trailing `word` rule consumes whole identifiers
 * so nothing can match the middle of a name.
 */
const RULES: Record<Lang, Rule[]> = {
  bash: [
    rule("#[^\\n]*", "c-comment"),
    rule("'[^']*'", "c-string"),
    rule('"(?:[^"\\\\]|\\\\.)*"', "c-string"),
    rule("\\$\\{?[A-Za-z_][\\w]*\\}?", "c-var"),
    rule("\\b(?:curl|psql|clickhouse-client|npx|saucer|echo|export)\\b", "c-cmd"),
    rule("--?[A-Za-z][\\w-]*", "c-flag"),
    rule("[A-Za-z_][\\w.-]*", "c-plain"),
  ],
  sql: [
    rule("--[^\\n]*", "c-comment"),
    // The parameter syntax is the thing worth seeing in a saucer snippet, so
    // it gets its own colour rather than reading as a brace and a name.
    rule("\\{\\{[^}]*\\}\\}", "c-param"),
    rule("'(?:[^'\\\\]|\\\\.)*'", "c-string"),
    rule(
      "\\b(?:SELECT|FROM|WHERE|GROUP\\s+BY|ORDER\\s+BY|LIMIT|AND|OR|AS|NOT|IN|JOIN|ON|WITH|INSERT|INTO|VALUES|CREATE|TABLE|ENGINE|PARTITION\\s+BY|TTL|INTERVAL|DESC|ASC|NULL|IF|EXISTS|UNION|ALL)\\b",
      "c-keyword",
    ),
    rule("\\b[A-Za-z_][\\w]*(?=\\s*\\()", "c-fn"),
    rule("\\b\\d+(?:\\.\\d+)?\\b", "c-number"),
    rule("[A-Za-z_][\\w]*", "c-plain"),
  ],
  json: [
    rule('"(?:[^"\\\\]|\\\\.)*"(?=\\s*:)', "c-key"),
    rule('"(?:[^"\\\\]|\\\\.)*"', "c-string"),
    rule("\\b(?:true|false|null)\\b", "c-literal"),
    rule("-?\\b\\d+(?:\\.\\d+)?\\b", "c-number"),
    rule("[{}\\[\\],:]", "c-punct"),
    rule("[A-Za-z_][\\w]*", "c-plain"),
  ],
  http: [
    rule("\\b(?:GET|POST|PUT|DELETE|PATCH)\\b", "c-cmd"),
    rule('"(?:[^"\\\\]|\\\\.)*"(?=\\s*:)', "c-key"),
    rule('"(?:[^"\\\\]|\\\\.)*"', "c-string"),
    rule("\\b\\d+(?:\\.\\d+)?\\b", "c-number"),
    rule("[{}\\[\\],:]", "c-punct"),
    rule("[A-Za-z_][\\w]*", "c-plain"),
  ],
};

/** Walks the source once, taking the first rule that matches at each offset. */
function highlight(source: string, lang: Lang): React.ReactNode[] {
  const rules = RULES[lang];
  const out: React.ReactNode[] = [];
  let plain = "";
  let index = 0;
  let key = 0;

  const flush = () => {
    if (plain) {
      out.push(plain);
      plain = "";
    }
  };

  while (index < source.length) {
    let matched = false;

    for (const { re, cls } of rules) {
      re.lastIndex = index;
      const found = re.exec(source);
      if (!found || found.index !== index) continue;

      // `c-plain` exists to consume whole identifiers so later rules cannot
      // match inside one; it carries no colour of its own.
      if (cls === "c-plain") {
        plain += found[0];
      } else {
        flush();
        out.push(
          <span key={key++} className={cls}>
            {found[0]}
          </span>,
        );
      }
      index += found[0].length;
      matched = true;
      break;
    }

    if (!matched) {
      plain += source[index];
      index += 1;
    }
  }

  flush();
  return out;
}

export interface CodeBlockProps {
  code: string;
  lang?: Lang;
  /** Shown in the chrome bar. A filename, a command, or what the snippet is. */
  title?: string;
  /** Traffic-light dots, for the blocks that stand alone as artwork. */
  chrome?: boolean;
}

export function CodeBlock({ code, lang = "bash", title, chrome = false }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  return (
    <figure className="code">
      {title || chrome ? (
        <figcaption className="code__bar">
          {chrome ? (
            <span className="code__dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          ) : null}
          {title ? <span className="code__title">{title}</span> : null}
          <button
            className="code__copy"
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(code).then(
                () => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1600);
                },
                () => undefined,
              );
            }}
          >
            {copied ? "copied" : "copy"}
          </button>
        </figcaption>
      ) : null}

      {/* Scrolls inside itself; the page never scrolls sideways and no line is
          ever clipped without a way to reach it. */}
      <pre className="code__pre">
        <code>{highlight(code, lang)}</code>
      </pre>
    </figure>
  );
}
